import React, { useEffect, useState } from "react";
import { UserPlus, Trash2, Loader2, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { extrasApi, bl } from "@/lib/extrasApi";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Avatar } from "@/components/find/atoms";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLES = ["admin", "member", "viewer"];

export default function Team() {
    const { locale } = useI18n();
    const { user } = useAuth();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try { setMembers(await extrasApi.team.list()); }
        catch (_e) { toast.error("Failed"); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function invite() {
        const email = window.prompt("Email to invite:");
        if (!email) return;
        try {
            await extrasApi.team.invite(email, "member");
            toast.success("Invitation sent");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }

    async function updateRole(id, role) {
        try {
            await extrasApi.team.updateRole(id, role);
            toast.success("Role updated");
            load();
        } catch (_e) { toast.error("Failed"); }
    }

    async function cancelInv(id) {
        try {
            await extrasApi.team.cancelInvite(id);
            toast.success("Invite cancelled");
            load();
        } catch (_e) { toast.error("Failed"); }
    }

    return (
        <div className="px-6 lg:px-10 py-6">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
                <div>
                    <h1 className="font-display font-black text-3xl tracking-tighter">{bl("Team", "Équipe", locale)}</h1>
                    <p className="mt-1 text-sm text-zinc-500">{bl("Invite teammates, manage roles, keep multi-tenant workspaces clean.", "Invite ton équipe, gère les rôles, workspaces multi-tenant propres.", locale)}</p>
                </div>
                {user?.role === "admin" && (
                    <button type="button" onClick={invite} data-testid="team-invite-btn" className="btn-cta text-white text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-2">
                        <UserPlus size={13} /> {bl("Invite member", "Inviter un membre", locale)}
                    </button>
                )}
            </div>

            {loading ? <Loader2 className="animate-spin text-zinc-500" /> : members.length === 0 ? (
                <div className="rounded-2xl p-10 text-center bg-brand-surface border border-white/8">
                    <UsersIcon size={22} className="mx-auto text-zinc-500 mb-2" />
                    <div className="text-sm text-zinc-500">{bl("No teammates yet.", "Aucun coéquipier.", locale)}</div>
                </div>
            ) : (
                <div className="rounded-2xl bg-brand-surface border border-white/8 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="text-xs uppercase text-zinc-500 border-b border-white/8"><tr>
                            <th className="p-3 text-left">Member</th><th className="p-3 text-left">Role</th><th className="p-3 text-left">Status</th><th className="p-3 w-10"></th>
                        </tr></thead>
                        <tbody>
                            {members.map(m => (
                                <tr key={m.user_id} className="border-b border-white/5" data-testid={`team-row-${m.user_id}`}>
                                    <td className="p-3">
                                        <div className="flex items-center gap-2">
                                            <Avatar seed={m.name} size={28} />
                                            <div>
                                                <div className="text-white">{m.name}</div>
                                                <div className="text-xs text-zinc-500">{m.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-3">
                                        {m.status === "pending" ? <span className="text-xs text-zinc-500 italic">{m.role}</span> : user?.role === "admin" && m.user_id !== user.user_id ? (
                                            <Select value={m.role} onValueChange={(v) => updateRole(m.user_id, v)}>
                                                <SelectTrigger className="w-[120px] h-8 bg-brand-void border-white/10"><SelectValue /></SelectTrigger>
                                                <SelectContent className="bg-brand-surface border-white/10 text-white">
                                                    {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        ) : <span className="text-xs">{m.role}</span>}
                                    </td>
                                    <td className="p-3">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${m.status === "active" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30"}`}>
                                            {m.status}
                                        </span>
                                    </td>
                                    <td className="p-3">
                                        {m.status === "pending" && user?.role === "admin" && (
                                            <button onClick={() => cancelInv(m.user_id)} className="text-zinc-500 hover:text-rose-300"><Trash2 size={14} /></button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
